import React, { useState, useEffect } from 'react';
import { TextField, IconButton, Box, Paper, Typography, Snackbar, Divider, Button, useMediaQuery, Skeleton, InputAdornment } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useTheme } from '@mui/material/styles';
import SearchRounded from '@mui/icons-material/SearchRounded';
import UploadFileOutlined from '@mui/icons-material/UploadFileOutlined';
import { Link } from 'react-router-dom';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import axios from '../services/api';
import Cart from './Cart';
import { getBackgroundColor, SchemeRibbon, ProductFormIcon } from './OrderParts';
import Fuse from 'fuse.js'; // Import Fuse.js
import './OrderPages.css';

const ProductSearch = ({ onAddToCart, cartItems = [],products=[],setProducts,onRemoveFromCart, onUpdateCartItem, onClearCart,customer }) => {
  const [query, setQuery] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);

  const [qty, setQty] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [loading, setLoading] = useState(false); // Add loading state for skeleton loader

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await axios.get('app_product_api.php');
        setProducts(response.data);  // Store fetched data in the App state
        setLoading(false);
      } catch (error) {
        console.error('Error fetching products:', error);
        setLoading(false);
        alert(error.message || 'Products could not be loaded.');
      }
    };

    fetchProducts();  // Fetch data when HomePage loads
  }, [setProducts]);
  useEffect(() => {
    const fetchProducts = () => {
      if (query.length >= 3) {
        setLoading(true); // Show loading

        // **Options for Fuse.js**
        const fuseOptions = {
          keys: ['product_name'], // Searchable field
          threshold: 0, // 0 = exact match, 1 = very lenient
          includeScore: false, // Do not show score in results
        };

        const fuse = new Fuse(products || [], fuseOptions); // Initialize Fuse.js with the product list

        // Perform the search
        let fuseResults = fuse.search(query).map((result) => result.item);

        // Sort matches (optional: sort alphabetically after scoring)
        fuseResults = fuseResults.sort((a, b) =>
          a.product_name.toLowerCase().localeCompare(b.product_name.toLowerCase())
        );

        // Limit to 30 results
        const limitedResults = fuseResults.slice(0, 30);

        setFilteredProducts(limitedResults); // Set filtered products
        setLoading(false); // Stop loading
      } else {
        setFilteredProducts([]); // Clear results if query is too short
      }
    };

    fetchProducts(); // Run the search function when `query` changes
  }, [query, products]);

  useEffect(() => {
    const initialQty = {};
    cartItems.forEach(item => {
      initialQty[item.product_id] = item.quantity;
    });
    setQty(initialQty);
  }, [cartItems]);

  const handleAddToCart = (product) => {
    const quantity = parseInt(qty[product.product_id], 10) || 0;
    if (quantity > 0) {
      onAddToCart({ ...product, quantity });
      setSnackbarOpen(true);
      setQuery('');
    }
  };

  const handleQtyChange = (productId, value) => {
    if (!isNaN(value) && value >= 0) {
      setQty({ ...qty, [productId]: value });
    }
  };

  const incrementQty = (productId) => {
    setQty({ ...qty, [productId]: (parseInt(qty[productId], 10) || 0) + 1 });
  };

  const decrementQty = (productId) => {
    setQty({ ...qty, [productId]: Math.max((parseInt(qty[productId], 10) || 0) - 1, 0) });
  };

  const isInCart = (productId) => {
    return cartItems.some(item => item.product_id === productId);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const searching = query.trim().length >= 3;

  return (
    <div className="order-page">
      <div className="order-search">
        <div className="order-search-card">
          <TextField
            label="Search products"
            placeholder="Type at least 3 letters of a product name"
            fullWidth
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={loading && !products?.length}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded fontSize="small" /></InputAdornment> }}
          />
          <div className="order-search-meta">
            <Button component={Link} to="/csv-order" size="small" variant="outlined" startIcon={<UploadFileOutlined />}>CSV order</Button>
            <span>{searching && !loading ? `${filteredProducts.length} ${filteredProducts.length === 1 ? 'product' : 'products'} found${filteredProducts.length === 30 ? ' (showing the first 30)' : ''}` : `${(products || []).length.toLocaleString('en-IN')} products available`}</span>
            <div className="stock-legend" aria-label="Stock colour key">
              <span><i style={{ background: getBackgroundColor(20) }} />In stock</span>
              <span><i style={{ background: getBackgroundColor(1) }} />Low stock (under 20)</span>
              <span><i style={{ background: getBackgroundColor(0) }} />Out of stock</span>
            </div>
          </div>
        </div>
      </div>

      <div className="product-results product-results--classic">
        {loading ? (
          Array.from(new Array(4)).map((_, index) => (
            <div className="product-card" key={index} style={{ padding: 16 }}>
              <Skeleton variant="text" width="45%" height={26} />
              <Skeleton variant="rounded" height={44} sx={{ mt: 1 }} />
            </div>
          ))
        ) : searching && !filteredProducts.length ? (
          <div className="order-empty"><strong>No products match “{query.trim()}”</strong>Check the spelling or try a shorter part of the name.</div>
        ) : (
          filteredProducts.map((product) => {
            const stockIsZero = Number(product.total_stock_qty) === 0;
            const inCart = isInCart(product.product_id);
            return (
              <Paper
                style={{
                  marginBottom: '1rem',
                  overflow: 'hidden',
                  borderRadius: '10px',
                  transition: 'transform 0.3s',
                  border: inCart ? '1px solid green' : '1px solid #d9e1e6',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
                  backgroundColor: getBackgroundColor(product.total_stock_qty), // Dynamic background color based on stock
                }}
                key={product.product_id}
              >
                <Box display="flex" flexDirection="column">
                  {/* Product Header */}
                  <Box padding="0.5rem 1rem" display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap="6px 12px">
                    <Typography variant="h6" style={{ fontWeight: 'bold', textAlign: 'left' }}>
                      {product.product_name}
                    </Typography>
                    <SchemeRibbon scheme={product.scheme} />
                  </Box>

                  {/* Quantity and Control Section */}
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexWrap="wrap"
                    padding="1rem"
                    position="relative"
                    style={{ backgroundColor: '#ffffff' }}
                  >
                    <ProductFormIcon name={product.product_name} />
                    <IconButton
                      onClick={() => decrementQty(product.product_id)}
                      style={{ color: '#ff5722', transition: 'transform 0.3s' }}
                      className="decrement-button"
                      disabled={stockIsZero}
                    >
                      <RemoveIcon />
                    </IconButton>
                    <TextField
                      value={qty[product.product_id] || '0'}
                      onChange={(e) => handleQtyChange(product.product_id, e.target.value)}
                      style={{ width: '50px', margin: '0 10px' }}
                      inputProps={{ min: 0, style: { textAlign: 'center' }, readOnly: stockIsZero }}
                      disabled={stockIsZero}
                    />
                    <IconButton
                      onClick={() => incrementQty(product.product_id)}
                      style={{ color: '#4caf50', transition: 'transform 0.3s' }}
                      className="increment-button"
                      disabled={stockIsZero}
                    >
                      <AddIcon />
                    </IconButton>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleAddToCart(product)}
                      startIcon={inCart ? <ChangeCircleIcon /> : <AddShoppingCartIcon />}
                      style={{ marginLeft: '10px' }}
                      disabled={stockIsZero}
                    >
                      {inCart ? 'Change Qty' : 'Add'}
                    </Button>
                  </Box>

                  {/* Divider */}
                  <Divider style={{ width: '100%' }} />

                  {/* Product Details in One Line */}
                  <Box
                    display="flex"
                    justifyContent="space-around"
                    alignItems="center"
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#f5f5f5',
                    }}
                  >
                    <Typography variant="body2" style={{ fontSize: isMobile ? '0.8rem' : '1rem', color: '#333' }}>
                      MRP: ₹{product.mrp}
                    </Typography>
                    <Typography variant="body2" style={{ fontSize: isMobile ? '0.8rem' : '1rem', color: '#333' }}>
                      PTR: ₹{product.ptr}
                    </Typography>
                    <Typography variant="body2" style={{ fontSize: isMobile ? '0.8rem' : '1rem', color: '#333' }}>
                      Stock: {product.total_stock_qty}
                    </Typography>
                    <Typography variant="body2" style={{ fontSize: isMobile ? '0.8rem' : '1rem', color: '#333' }}>
                      GST: {product.gst}%
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            );
          })
        )}
      </div>

      <Cart
        cartItems={cartItems}
        onRemoveFromCart={onRemoveFromCart}
        onUpdateCartItem={onUpdateCartItem}
        onClearCart={onClearCart}
        customer={customer}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message="Item quantity updated in cart"
      />
    </div>
  );
};

export default ProductSearch;
